<script setup>
import {onBeforeMount, ref, reactive} from "vue";
import YTCard from "@/components/YTCard/YTCard.vue";
import {getRoute} from "@/hooks/getRoute.js";
import moment from "moment"
import router from "@/router/index.js";
import YTCheckedPanel from "@/components/YTCheckedPanel/index.vue"
import YTCourseSignOrderState from "@/components/YTCourseSignOrderState/index.vue"
import {detailClubCourseOrder, updateClubCourseOrder} from "@/api/club/club.js";
import {courseSignOrderStateEnum} from "@/enum/courseSignOrderStateEnum.js";
import {detailMember} from "@/api/member/member.js";
import {itemDetail} from "@/api/goods/goods.js";
import YTDrawerItem from "@/components/YTDrawerItem/YTDrawerItem.vue";
import YTDrawerMember from "@/components/YTDrawerMember/YTDrawerMember.vue";

// 路由信息
const {route} = getRoute();

// 是否有报名id
const hasOrderId = ref(false)
// 加载状态
const loading = ref(false)

onBeforeMount(async () => {
  hasOrderId.value = Boolean(route.query.orderId)
  // 检测是否携带报名id
  if (!hasOrderId.value) {
    loading.value = true
    ElMessage.error("暂无报名信息")
    return
  }
  await handleSearch()
})

// 获取订单信息
const handleSearch = async () => {
  try {
    loading.value = true
    const res = await detailClubCourseOrder(route.query.orderId)
    formData.orderId = res.orderId
    formData.orderTime = moment(res.orderTime).format("YYYY-MM-DD HH:mm:ss")
    formData.nickname = res.nickname
    formData.memberId = res.memberId
    formData.phone = res.phone
    formData.activityImage = res.orderItem && res.orderItem.goodsImage ? res.orderItem.goodsImage : ""
    formData.activityName = res.orderItem && res.orderItem.goodsName ? res.orderItem.goodsName : "-"
    formData.city = res.orderItem && res.orderItem.city ? res.orderItem.city : "暂无"
    formData.usageCount = res.orderItem && res.orderItem.usageCount ? res.orderItem.usageCount : "-"
    formData.payableAmount = res.payableAmount
    formData.receivedAmount = res.receivedAmount
    formData.orderNo = res.orderNo
    formData.paymentMethod = "微信支付"
    formData.orderState = res.orderState
    formData.orderItem = res.orderItem
    triggerList.value = []
    signTrigger.value.forEach(i => {
      let param = {
        value: i.stateId,
        name: i.stateName
      }
      triggerList.value.push(param)
    })
    loading.value = false
  } catch (e) {
    console.log(e)
  }
}

/*订单相关*/
const signTrigger = ref(courseSignOrderStateEnum)
const triggerList = ref([])
// 订单详情信息
const formData = reactive({
  orderId: null,
  orderTime: null,
  nickname: null,
  memberId: null,
  phone: null,
  activityImage: null,
  activityName: null,
  city: null,
  usageCount: null,
  payableAmount: 0,
  receivedAmount: 0,
  orderNo: null,
  paymentMethod: "微信支付",
  state: null
})
// 用户信息抽屉打开状态
const drawer = ref(false)
// 处理点击用户名称跳转
const handleCustomerDetail = async (id) => {
  drawer.value = true
  drawerCacheType.value = 1
  drawerLoading.value = true
  drawerCacheInfo.value = await detailMember(id)
  drawerLoading.value = false
}
// 处理点击活动信息跳转
const handleActivityDetail = async () => {
  drawer.value = true
  drawerCacheType.value = 2
  drawerLoading.value = true
  drawerCacheInfo.value = await itemDetail(formData.orderItem.goodsId)
  drawerLoading.value = false
}
// 处理关闭用户信息抽屉
const handleCloseDrawer = () => {
  drawerCacheType.value = null
  drawerCacheInfo.value = null
  drawer.value = false
}
// 抽屉缓存类型
const drawerCacheType = ref(null)
// 抽屉缓存信息
const drawerCacheInfo = ref(null)
// 抽屉缓存加载状态
const drawerLoading = ref(false)

/*保存修改相关*/
// 保存内容提交表单
const submit = async (type) => {
  try {
    loading.value = true
    const param = {
      state: type
    }
    await updateClubCourseOrder(formData.orderId, param)
    loading.value = false
    ElMessage.success("修改订单状态成功")
    router.push("/listClubCourseSign")
  } catch (e) {
    console.log(e)
  }
}

/*页面动画相关*/
// 基本信息锚点
const baseRef = ref()

/*更新状态相关*/
// 多选弹窗打开状态
const open = ref(false)
// 处理多选框取消
const handleCancelPanel = () => {
  open.value = false
}
// 处理多选框确认
const handleConfirmPanel = async (val) => {
  await submit(val[0])
  open.value = false
}
</script>

<template>
  <el-form
      :model="formData"
      label-position="right"
      label-width="100px"
  >
    <div class="container_box mg_b_2" style="padding-bottom: 0;">
      <YTCard ref="baseRef" :title="'基本信息'" :is-show-divider="false" :is-show-title-line="true" :is-retract="false" :isCollapsed="true">
        <template #container>
          <v-skeleton-loader
              :loading="loading"
              type="list-item-three-line"
          >
            <div>
              <el-form-item label="订单编号：" prop="orderId">
                <el-text class="mx-1" v-text="formData.orderId"></el-text>
              </el-form-item>
              <el-form-item label="时间：" prop="orderTime">
                <el-text class="mx-1" v-text="formData.orderTime"></el-text>
              </el-form-item>
              <el-form-item label="用户：" prop="nickname">
                <el-link type="primary" v-text="`${formData.nickname}（${formData.phone}）`" :underline="false" @click="handleCustomerDetail(formData.memberId)"></el-link>
              </el-form-item>
            </div>
          </v-skeleton-loader>
        </template>
      </YTCard>
    </div>
    <div class="container_box mg_b_2" style="padding-bottom: 0">
      <YTCard :title="'课程信息'" :is-show-divider="false" :is-show-title-line="true" :is-retract="false" :isCollapsed="true">
        <template #container>
          <v-skeleton-loader
              :loading="loading"
              type="list-item-avatar-three-line"
          >
            <div class="w-100 mx-5">
              <div class="flex cursor-pointer" @click="handleActivityDetail">
                <el-image class="mg_r_1" style="width: 100px; height: 100px" :src="formData.activityImage" :fit="'contain'" />
                <div class="flex_align_t flex_warp flex_1">
                  <div class="w-100">
                    <strong>
                      <el-text class="mx-1" size="large" v-text="formData.activityName"></el-text>
                    </strong>
                    <div>
                      <el-text class="mx-1" type="info" size="small" v-text="`城市：${formData.city}`"></el-text>
                    </div>
                    <div>
                      <el-text class="mx-1" type="info" size="small" v-if="formData.orderItem.validityType === 'PERMANENT'" v-text="`有效期：长期有效`"></el-text>
                      <el-text class="mx-1" type="info" size="small" v-if="formData.orderItem.validityType === 'DAYS'" v-text="`有效期：${formData.orderItem.validityValue}天`"></el-text>
                      <el-text class="mx-1" type="info" size="small" v-if="formData.orderItem.validityType === 'END_DATE'" v-text="`有效期：${formData.orderItem.validityValue}`"></el-text>
                    </div>
                    <div>
<!--                      <el-text class="mx-1" type="info" size="small" v-text="`使用情况：${formData.orderItem.remaining}/${formData.usageCount}`"></el-text>-->
                      <el-text class="mx-1" type="info" size="small" v-text="`使用情况：${formData.orderItem.used}/${formData.usageCount}`"></el-text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-skeleton-loader>
        </template>
      </YTCard>
    </div>
    <div class="container_box mg_b_2" style="padding-bottom: 0">
      <YTCard :title="'支付信息'" :is-show-divider="false" :is-show-title-line="true" :is-retract="false" :isCollapsed="true">
        <template #container>
          <v-skeleton-loader
              :loading="loading"
              type="list-item-three-line"
          >
            <div>
              <el-form-item label="商品总价：" prop="payableAmount">
                <el-text class="mx-1" v-text="formData.payableAmount"></el-text>
              </el-form-item>
              <el-form-item label="实付金额：" prop="receivedAmount">
                <el-text class="mx-1" v-text="formData.receivedAmount"></el-text>
              </el-form-item>
              <el-form-item label="交易编号：" prop="orderNo">
                <el-input class="mx-1" :disabled="true" v-model="formData.orderNo"></el-input>
              </el-form-item>
              <el-form-item label="支付类型：" prop="paymentMethod">
                <el-text class="mx-1" v-text="formData.paymentMethod"></el-text>
              </el-form-item>
            </div>
          </v-skeleton-loader>
        </template>
      </YTCard>
    </div>
    <div class="container_box mg_b_2" style="padding-bottom: 0">
      <YTCard :title="'报名状态'" :is-show-divider="false" :is-show-title-line="true" :is-retract="false" :isCollapsed="true">
        <template #container>
          <v-skeleton-loader
              :loading="loading"
              type="list-item"
          >
            <el-form-item label="报名状态：" prop="orderState">
              <YTCourseSignOrderState class="mg_r_2" :state="formData.orderState"></YTCourseSignOrderState>
              <v-btn color="success" @click="open = true" :disabled="!$handleHasBtnPermission('all', 'courseOrder:edit')">更新状态</v-btn>
            </el-form-item>
          </v-skeleton-loader>
        </template>
      </YTCard>
    </div>
    <div>
      <v-row>
        <v-col>
          <v-btn @click="router.back()">返回</v-btn>
        </v-col>
      </v-row>
    </div>
  </el-form>
  <YTCheckedPanel
      v-if="open"
      :open="open"
      :title="'订单状态'"
      :list="triggerList"
      :hasAll="false"
      :checked-limit="1"
      @cancel="handleCancelPanel"
      @confirm="handleConfirmPanel">
  </YTCheckedPanel>
  <el-drawer
      v-model="drawer"
      :title="drawerCacheType === 1 ? '用户信息' : '课程信息'"
      direction="rtl"
      :close="handleCloseDrawer"
      style="text-align: left"
      :size="drawerCacheType === 1 ? '30%' : '50%'"
  >
    <el-skeleton v-if="drawerLoading" :rows="5" animated />
    <div v-else>
      <div v-if="drawerCacheType === 1">
        <YTDrawerMember :drawer-cache-info="drawerCacheInfo"></YTDrawerMember>
      </div>
      <div v-else>
        <YTDrawerItem :drawer-cache-info="drawerCacheInfo"></YTDrawerItem>
      </div>
    </div>
  </el-drawer>
</template>

<style scoped>

</style>